import { useRef, useState, useMemo } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import clsx from "clsx";
import { decryptEnv } from "@/helpers";
import Skeleton from "../Skeleton";

interface TextFieldProps {
  label?: React.ReactNode;
  className?: string;
  containerClass?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  nobg?: boolean;
  value?: string | number;
  rightIcon?: React.ReactNode;
  title?: string;
  validateInput?: boolean;
  scrollable?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  type?: string;
  placeholder?: string;
  isApiKey?: boolean;
  [key: string]: any;
}

const TextField = ({
  label,
  className,
  containerClass,
  helperText,
  error,
  nobg = false,
  value,
  rightIcon,
  title,
  validateInput = false,
  scrollable = false,
  onChange,
  isApiKey = false,
  ...props
}: TextFieldProps) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [decryptedValue, setDecryptedValue] = useState<string | null>(null);

  const sanitizeRegex = useMemo(() => /[^a-zA-Z-]/g, []);

  const handleToggle = async () => {
    if (isApiKey) {
      setShowPassword(!showPassword);

      if (!showPassword && !decryptedValue && value) {
        setIsDecrypting(true);
        try {
          const decrypted = await decipherApiKey(value as string);
          setDecryptedValue(decrypted);
        } catch (error) {
          console.error("Failed to decrypt API key:", error);
        } finally {
          setIsDecrypting(false);
        }
      }

      if (ref.current) {
        ref.current.type = !showPassword ? "text" : "password";
      }
    } else {
      setShowPassword(!showPassword);
      if (ref.current) {
        ref.current.type = showPassword ? "password" : "text";
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (validateInput) {
      const sanitizedValue = e.target.value.replace(sanitizeRegex, "");
      e.target.value = sanitizedValue;
    }
    onChange?.(e);

    if (isApiKey) {
      setDecryptedValue(null);
    }
  };

  const isCommand =
    props.name?.includes("command") || props.name?.includes("script") || false;

  const formatShellScript = (cmd: string): string => {
    if (!cmd) return "";

    let shebang = "";
    if (cmd.startsWith("#!")) {
      const shebangEnd = cmd.indexOf("\n");
      if (shebangEnd > -1) {
        shebang = cmd.substring(0, shebangEnd + 1);
        cmd = cmd.substring(shebangEnd + 1);
      }
    }

    const formatted = cmd
      .replace(/\s*&&\s*/g, " &&\n")
      .replace(/\s*;\s*/g, ";\n")
      .replace(/\s*\|\|\s*/g, " ||\n")
      .replace(/\s*\|\s*/g, " |\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line)
      .join("\n");

    return shebang + formatted;
  };

  const decipherApiKey = async (apiKey: string): Promise<string> => {
    const decrypted = await decryptEnv([{ name: "API_KEY", value: apiKey }]);
    return decrypted[0].value;
  };

  // Determine the display value
  const displayValue =
    isApiKey && showPassword && decryptedValue ? decryptedValue : value;

  return (
    <div className={clsx("form-control rounded-none w-full", containerClass)}>
      {label && (
        <label
          className={clsx("label text-[14px]", {
            "text-error is-inValid": error,
          })}
          htmlFor={props.name}
        >
          {label}
        </label>
      )}
      <div className="relative w-full dark:border-theme-color-light">
        <div
          className={clsx("w-full", {
            "overflow-x-auto": scrollable,
          })}
        >
          {isApiKey && isDecrypting ? (
            <div className="input rounded-none input-bordered input-lg w-full h-[54px] flex items-center">
              <Skeleton className="w-full h-4" />{" "}
            </div>
          ) : (
            <input
              ref={ref}
              title={title}
              required
              autoCorrect="off"
              autoComplete="off"
              autoCapitalize="off"
              {...props}
              value={displayValue}
              onChange={handleChange}
              className={clsx(
                "input rounded-none input-bordered input-lg w-full h-[54px]",
                {
                  "bg-light2": !nobg,
                  "!border-error text-error placeholder-error is-inValid":
                    error,
                  "border-primary": !error,
                  "whitespace-nowrap min-w-full": scrollable,
                },
                "focus:outline-none dark:bg-theme-color disabled:dark:bg-theme-color/50 disabled:bg-light/50",
                className,
              )}
              style={
                scrollable
                  ? { overflow: "auto", textOverflow: "initial" }
                  : undefined
              }
            />
          )}
        </div>
        <span className="absolute top-0 right-0 flex items-center justify-center h-full px-3 z-10">
          {rightIcon ||
            ((props.type === "password" || isApiKey) &&
              (isDecrypting ? (
                <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              ) : (
                <button
                  onClick={handleToggle}
                  className="cursor-pointer text-primary"
                  type="button"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              )))}
        </span>
      </div>

      {isCommand && value && (
        <div className="mt-2 border rounded dark:border-theme-color-light overflow-hidden">
          <div className="bg-gray-100 dark:bg-theme-color-dark p-2 text-xs font-semibold border-b dark:border-theme-color-light">
            Command Preview
          </div>
          <pre className="p-3 text-sm whitespace-pre overflow-auto max-h-56 bg-gray-50 dark:bg-gray-900 dark:text-gray-100">
            {formatShellScript(String(value))}
          </pre>
        </div>
      )}

      {Boolean(helperText) && (
        <div
          className={clsx("my-2 text-[14px]", {
            "text-error is-inValid": error,
            "border-primary !text-black dark:!text-white": !error,
          })}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default TextField;
